import React, { useCallback, useEffect, useState } from 'react';
import { PageCommonProps } from '@meettry/company/route/route';
import { LoginStatus, useLoginStatus } from '@meettry/ui-components/utils/firebase';
import NotFoundPage from '@meettry/ui-components/components/pages/NotFound';
import { Breadcrumb } from '@meettry/company/component/molecules/breadcrumb';
import useSearchQueryManager from '@meettry/ui-components/hooks/useSearchQueryManager';
import { SEARCH_TYPE } from '@meettry/ui-components/constants';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import {
  BOOKMARK_USER_BY_ENTERPRISE_QUERY,
  SEARCH_BY_ENTERPRISE_QUERY,
  SearchBhyEnterpriseQueryResponse,
  UNBOOKMARK_USER_BY_ENTERPRISE_QUERY
} from '@meettry/ui-components/queries';
import { useHistory } from 'react-router-dom';
import Loader from '@meettry/ui-components/components/atoms/Loader';
import Pagination from '@meettry/ui-components/components/organisms/Pagination';
import styled from 'styled-components';
import CassetteUser from '@meettry/ui-components/components/organisms/CassetteUser';
import CassetteResume from '@meettry/ui-components/components/organisms/CassetteResume';
import CassetteChallenge from '@meettry/ui-components/components/organisms/CassetteChallenge';
import iconPathNoData from '@meettry/ui-components/images/icons/primary/icon_no_data.svg';
import { simplifyDigits } from '@meettry/ui-components/utils';
import usePopup from '@meettry/company/hooks/usePopup';

const TAB_SETTINGS: any = {
  USER: {
    type: SEARCH_TYPE.USER,
    label: 'User',
    order: 0
  },
  RESUME: {
    type: SEARCH_TYPE.RESUME,
    label: 'Resume',
    order: 1
  },
  CHALLENGE: {
    type: SEARCH_TYPE.CHALLENGE,
    label: 'Challenge',
    order: 2
  },
  CHAT: {
    type: SEARCH_TYPE.CHAT,
    label: 'Chat',
    order: 3
  }
};

/**
 * SearchPage
 * 検索結果画面
 */
const SearchPage: React.FC<PageCommonProps> = () => {
  const history = useHistory();
  const [targetUser, setTargetUser] = useState<{ userId: string; nickname: string } | null>(null);
  const [bookmarkUsers, updateBookmarkUsers] = useState<string[]>([]);
  const { status } = useLoginStatus();
  const { showSuccessPopup, showErrorPopup } = usePopup();
  const pageLoading = status === LoginStatus.NotInitialized;
  const canViewPage = status === LoginStatus.LoginNicknameRegistered;
  const {
    queryString,
    displayNumber,
    page,
    type,
    sortType,
    changeSortType,
    changeQuery,
    resetQuery,
    createHistoryContentWithSearchQuery
  } = useSearchQueryManager();

  /**
   * 企業側検索クエリー
   */
  const [
    doSearchQuery,
    { data: searchResults, loading: searchResultsLoading, error: searchResultsError }
  ] = useLazyQuery<SearchBhyEnterpriseQueryResponse>(SEARCH_BY_ENTERPRISE_QUERY);

  /**
   * エンジニアお気に入りミューテーション
   */
  const [
    mutateBookmark,
    { data: bookmarkMutationResult, loading: bookmarkLoading, error: bookmarkError }
  ] = useMutation(BOOKMARK_USER_BY_ENTERPRISE_QUERY, {
    onCompleted: ({ bookmarkUser }) => {
      if (!bookmarkUser?.ok) {
        showErrorPopup(`${targetUser?.nickname ?? ''}をお気に入りに追加できませんでした`);
      } else {
        showSuccessPopup(`${targetUser?.nickname ?? ''}をお気に入りに追加しました`);
        if (targetUser) updateBookmarkUsers([...bookmarkUsers, targetUser.userId]);
      }
      setTargetUser(null);
    },
    onError: (error) => {
      showErrorPopup(`${targetUser?.nickname ?? ''}をお気に入りに追加できませんでした`);
      setTargetUser(null);
    }
  });

  /**
   * エンジニアお気に入り外しミューテーション
   */
  const [
    mutateUnbookmark,
    { data: unbookmarkMutationResult, loading: unbookmarkLoading, error: unbookmarkError }
  ] = useMutation(UNBOOKMARK_USER_BY_ENTERPRISE_QUERY, {
    onCompleted: ({ unbookmarkUser }) => {
      if (!unbookmarkUser?.ok) {
        showErrorPopup(`${targetUser?.nickname ?? ''}をお気に入りから外せませんでした`);
      } else {
        showSuccessPopup(`${targetUser?.nickname ?? ''}をお気に入りから外しました`);
        if (targetUser) updateBookmarkUsers(bookmarkUsers.filter((u) => u !== targetUser.userId));
      }
      setTargetUser(null);
    },
    onError: (error) => {
      showErrorPopup(`${targetUser?.nickname ?? ''}をお気に入りから外せませんでした`);
      setTargetUser(null);
    }
  });

  /**
   * ページ遷移
   */
  const goToUserPage = useCallback((nickname: string) => {
    history.push(`/user/${nickname}/dashboard`);
  }, []);
  const goToUserResumePage = useCallback((nickname: string) => {
    history.push(`/user/${nickname}/resume`);
  }, []);
  const goToUserChallengePage = useCallback((nickname: string) => {
    history.push(`/user/${nickname}/challenge`);
  }, []);
  const goToChatPage = useCallback(() => {
    history.push(`/user/chat`);
  }, []);

  /**
   * エンジニアのブックマーク
   */
  const bookmarkEngineer = (userId: string, nickname: string, isUserBookmark: boolean) => {
    if (bookmarkLoading || unbookmarkLoading) return;
    setTargetUser({ userId, nickname });
    if (isUserBookmark) {
      mutateUnbookmark({
        variables: { userId }
      });
    } else {
      mutateBookmark({
        variables: { userId }
      });
    }
  };

  /**
   * エンジニアにメッセージを送る
   */
  const clickMessage = (userId: string, nickname: string) => {
    goToChatPage();
  };

  /**
   * 検索クエリー情報をURLへ反映
   */
  useEffect(() => {
    const historyContent = createHistoryContentWithSearchQuery('/search');
    if (!historyContent) return;
    history.replace(historyContent);
  }, [queryString, displayNumber, page, type]);

  /**
   * 検索結果をフェッチ
   */
  useEffect(() => {
    if (!queryString) return;
    if (!canViewPage) return;

    doSearchQuery({
      variables: {
        queryString,
        first: displayNumber,
        offset: Number(displayNumber) * (Number(page) - 1),
        field: sortType
      }
    });
  }, [queryString, displayNumber, sortType, page, canViewPage]);
  const userResult = searchResults?.searchFor.users;
  const resumeResult = searchResults?.searchFor.resumes;
  const challengeResult = searchResults?.searchFor.challenges;
  const chatMessageResult = searchResults?.searchFor.chatMessages;

  /**
   * bookmarkUser の更新
   */
  useEffect(() => {
    if (!searchResultsLoading && !!searchResults) {
      updateBookmarkUsers(
        searchResults?.loginSession.user.organization.bookmarks.users.results.map((r) => r.id) ?? []
      );
    }
  }, [searchResults, searchResultsLoading]);

  /**
   * 検索カテゴリと検索結果のカウント
   */
  const searchCategory = [
    {
      type: TAB_SETTINGS.USER.type,
      label: TAB_SETTINGS.USER.label,
      count: userResult?.count ?? 0
    },
    {
      type: TAB_SETTINGS.RESUME.type,
      label: TAB_SETTINGS.RESUME.label,
      count: resumeResult?.count ?? 0
    },
    {
      type: TAB_SETTINGS.CHALLENGE.type,
      label: TAB_SETTINGS.CHALLENGE.label,
      count: challengeResult?.count ?? 0
    }
    // {
    //   type: TAB_SETTINGS.CHAT.type,
    //   label: TAB_SETTINGS.CHAT.label,
    //   count: chatMessageResult?.count ?? 0
    // },
  ];
  const activeCount = searchCategory.find((c) => c.type === type)?.count ?? 0;

  /**
   * 検索結果の一覧コンポーネント
   */
  const viewList = (type: string) => {
    switch (type) {
      case SEARCH_TYPE.RESUME: {
        if (!resumeResult || resumeResult.results.length === 0)
          return (
            <StyledSearchNoData>
              <img src={iconPathNoData} width="60" alt="NO DATA" />
              <p>条件に一致する検索結果はありません</p>
              <p>キーワードを確認・変更して検索し直してください。</p>
            </StyledSearchNoData>
          );
        return (
          <StyledSearchResultList>
            {resumeResult.results.map((resume) => (
              <li key={`resume_${resume.id}`}>
                <CassetteResume
                  resume={resume}
                  searchWord={resumeResult.queryString}
                  onClick={goToUserResumePage}
                />
              </li>
            ))}
          </StyledSearchResultList>
        );
      }
      case SEARCH_TYPE.CHALLENGE: {
        if (!challengeResult || challengeResult.results.length === 0)
          return (
            <StyledSearchNoData>
              <img src={iconPathNoData} width="60" alt="NO DATA" />
              <p>条件に一致する検索結果はありません</p>
              <p>キーワードを確認・変更して検索し直してください。</p>
            </StyledSearchNoData>
          );
        return (
          <StyledSearchResultList>
            {challengeResult.results.map((challenge) => (
              <li key={`challenge_${challenge.id}`}>
                <CassetteChallenge
                  challenge={challenge}
                  searchWord={challengeResult.queryString}
                  onClick={goToUserChallengePage}
                />
              </li>
            ))}
          </StyledSearchResultList>
        );
      }
      case SEARCH_TYPE.CHAT: {
        if (!chatMessageResult || chatMessageResult.results.length === 0)
          return (
            <StyledSearchNoData>
              <img src={iconPathNoData} width="60" alt="NO DATA" />
              <p>条件に一致する検索結果はありません</p>
              <p>キーワードを確認・変更して検索し直してください。</p>
            </StyledSearchNoData>
          );
        return (
          <StyledSearchResultList>
            {chatMessageResult.results.map((chat) => (
              <li key={`chat_${chat.roomId}_${chat.senderId}_${chat.createdAt}`}>
                {JSON.stringify(chat)}
              </li>
            ))}
          </StyledSearchResultList>
        );
      }
      case SEARCH_TYPE.USER:
      default: {
        if (!userResult || userResult.results.length === 0)
          return (
            <StyledSearchNoData>
              <img src={iconPathNoData} width="60" alt="NO DATA" />
              <p>条件に一致する検索結果はありません</p>
              <p>キーワードを確認・変更して検索し直してください。</p>
            </StyledSearchNoData>
          );
        return (
          <StyledSearchResultList>
            {userResult.results.map((user) => (
              <li key={`user_${user.id}`}>
                <CassetteUser
                  userInfo={user}
                  searchWord={userResult.queryString}
                  onClick={goToUserPage}
                  onClickBookmark={bookmarkEngineer}
                  onClickMessage={clickMessage}
                  isLoginUserCompany={true}
                  isBookmark={bookmarkUsers.includes(user.id) ?? false}
                  buttonStyle={{ size: 'M', width: 100 }}
                />
              </li>
            ))}
          </StyledSearchResultList>
        );
      }
    }
  };

  const handleChangeTab = (type: string) => () => {
    resetQuery('page');
    changeQuery('type')(type);
  };

  return pageLoading ? (
    <StyledContents className="searchPage" />
  ) : canViewPage ? (
    <StyledContents className="searchPage">
      <StyledBreadcrumbArea>
        <Breadcrumb />
      </StyledBreadcrumbArea>
      <StyledContentInner>
        <StyledResultLayout>
          <StyledSearchCategoryList>
            {searchCategory.map((r) => (
              <li key={r.type}>
                <StyledSearchCategory
                  data-active={type == r.type}
                  data-count={r.count}
                  onClick={handleChangeTab(r.type)}
                >
                  <span>{r.label}</span>
                  <span>{simplifyDigits(r.count)}</span>
                </StyledSearchCategory>
              </li>
            ))}
          </StyledSearchCategoryList>
          <div>
            <StyledSearchContent>
              <StyledSearchHeader>
                <StyledSearchHeading>
                  {queryString} <span>の検索結果</span>
                </StyledSearchHeading>
                <div>
                  <label htmlFor="sort-search">表示順</label>
                  <StyledSortSelect style={{ marginLeft: '10px' }}>
                    <select
                      defaultValue={sortType}
                      id="sort-search"
                      onChange={(e) => changeSortType(e.target.value)}
                    >
                      <option value="updated">更新日順</option>
                      <option value="created">作成日順</option>
                    </select>
                  </StyledSortSelect>
                </div>
              </StyledSearchHeader>
              {searchResultsLoading && <Loader />}
              {!searchResultsLoading && viewList(String(type))}
            </StyledSearchContent>
            {activeCount > 10 && (
              <Pagination
                count={activeCount}
                showCount={Number(displayNumber)}
                onClick={(i) => {
                  changeQuery('page')(String(i));
                  window.scrollTo(0, 0);
                }}
                offset={Number(page)}
              />
            )}
          </div>
        </StyledResultLayout>
      </StyledContentInner>
    </StyledContents>
  ) : (
    <NotFoundPage />
  );
};
export default SearchPage;

const StyledContents = styled.div`
  && {
    padding: 10px 50px 50px;
    background-color: #f7f7f7;
    flex: 1;
    min-height: calc(100vh - 96px);
  }
`;

const StyledContentInner = styled.div`
  && {
    margin-top: 50px;
    max-width: 1200px;
    min-width: 900px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const StyledBreadcrumbArea = styled.div`
  margin-left: -40px;
  margin-right: -40px;
`;

const StyledResultLayout = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;

  & > *:nth-child(1) {
    box-sizing: border-box;
    width: 240px;
  }

  & > *:nth-child(2) {
    width: calc(100% - 300px);
    margin-left: 60px;
  }
`;

const StyledSearchCategoryList = styled.ul`
  border-top: 1px solid #dddddd;
  border-bottom: 1px solid #dddddd;
  background-color: #ffffff;

  & > li + li {
    border-top: 1px solid #dddddd;
  }
`;

const StyledSearchCategory = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding-left: 20px;
  padding-right: 10px;
  transition: background 0.15s;

  & > *:nth-child(1) {
    font-size: 18px;
    font-weight: bold;
    color: #2459bf;
  }

  & > *:nth-child(2) {
    padding: 4px 10px;
    background-color: #fee8eb;
    color: #f4233c;
    font-size: 14px;
    font-weight: bold;
    line-height: 1;
    border-radius: 20px;
  }

  &[data-active='true'] {
    background-color: #0fafa9;

    & > *:nth-child(1) {
      color: #ffffff;
    }
  }

  &[data-count='0'] {
    & > *:nth-child(2) {
      background-color: #dddddd;
      color: #999999;
    }
  }

  &:not([data-active='true']):hover {
    cursor: pointer;
    background-color: #e6f7f6;
  }
`;

const StyledSearchContent = styled.div`
  padding: 40px 50px 60px;
  background-color: #ffffff;
`;

const StyledSearchHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledSearchHeading = styled.h1`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  color: #333333;
  span {
    display: inline-block;
    font-size: 16px;
    margin-left: 10px;
    line-height: 1;
  }
`;

const StyledSelect = styled.div`
  position: relative;
  display: inline-block;

  & > select {
    appearance: none;
    outline: none;
    box-sizing: border-box;
    display: block;
    width: 100%;
    font-size: 16px;
    line-height: 1;
    border: 1px solid #eeeeee;
    padding: 8px 30px 8px 10px;
  }

  &:after {
    pointer-events: none;
    content: '';
    display: block;
    width: 5px;
    height: 5px;
    border-right: 1px solid #707070;
    border-bottom: 1px solid #707070;
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translate(0, -75%) rotate(45deg);
  }
`;

const StyledSortSelect = styled(StyledSelect)`
  & > select {
    font-size: 14px;
    color: #0fafa9;
  }
`;

const StyledSearchResultList = styled.ul`
  cursor: pointer;
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  margin-top: 10px;

  & > li {
    transition: background 0.15s;
  }

  & > li:hover {
    background-color: #e6f7f6;
  }

  & > li + li {
    border-top: 1px solid #eeeeee;
  }
`;

const StyledSearchNoData = styled.div`
  padding: 60px;
  border: 1px solid #eeeeee;
  background-color: #f2fbfb;
  text-align: center;
  margin-top: 40px;

  & > *:nth-child(2) {
    font-size: 20px;
    color: #0fafa9;
    font-weight: bold;
    margin-top: 30px;
  }
  & > *:nth-child(3) {
    font-size: 16px;
    color: #333333;
    margin-top: 20px;
  }
`;
